import { Command } from "./Command";
import { HistoryEventHandler } from "../history";
import { setHref, getHref, getRotationAngle } from "../utilities";
import { removeElementFromListMap } from "../svgtransformlist";
import { CommandAttributes, replaceChildren, serializeChildren } from "./CommandAttributes";

export type { CommandAttributes } from "./CommandAttributes";

/**
* History command to make a change to an element.
* Usually an attribute change, but can also be textcontent.
* @implements {module:history.HistoryCommand}
*/
export class ChangeElementCommand extends Command {
    public newValues: CommandAttributes;
    public oldValues: CommandAttributes;

    /**
    * @param {SVGElement} elem - The DOM element that was changed
    * @param {module:history.CommandAttributes} attrs - Attributes to be changed with the values they had *before* the change
    * @param {string} [text] - An optional string visible to user related to this change
     */
    constructor(elem: SVGElement, attrs: CommandAttributes, text?: string) {
        super();
        this.elem = elem;
        this.text = text ? ('Change ' + elem.tagName + ' ' + text) : ('Change ' + elem.tagName);
        this.newValues = {};
        this.oldValues = attrs;
        for (const attr in attrs) {
            if (attr === '#text') {
                this.newValues[attr] = elem.textContent;
            } else if (attr === '#href') {
                this.newValues[attr] = getHref(elem);
            } else if (attr === '#children') {
                this.newValues[attr] = serializeChildren(elem);
            } else {
                this.newValues[attr] = elem.getAttribute(attr);
            }
        }
    }

    /**
    * Performs the stored change action.
    * @param {module:history.HistoryEventHandler} handler
    * @fires module:history~Command#event:history
    * @returns {void}
    */
    apply(handler: HistoryEventHandler) {
        super.apply(handler, () => {
            let bChangedTransform = false;
            Object.entries(this.newValues).forEach(([attr, value]) => {
                if (attr === '#children') {
                    replaceChildren(this.elem, value);
                } else if (value) {
                    if (attr === '#text') {
                        this.elem.textContent = value.toString();
                    } else if (attr === '#href') {
                        setHref(this.elem, value.toString());
                    } else {
                        this.elem.setAttribute(attr, value.toString());
                    }
                } else if (attr === '#text') {
                    this.elem.textContent = '';
                } else {
                    this.elem.setAttribute(attr, '');
                    this.elem.removeAttribute(attr);
                }

                if (attr === 'transform') { bChangedTransform = true; }
            });

            // relocate rotational transform, if necessary
            if (!bChangedTransform) {
                const angle = getRotationAngle(this.elem as SVGGraphicsElement);
                if (angle) {
                    const bbox = (this.elem as SVGGraphicsElement).getBBox();
                    const cx = bbox.x + bbox.width / 2;
                    const cy = bbox.y + bbox.height / 2;
                    const rotate = ['rotate(', angle, ' ', cx, ',', cy, ')'].join('');
                    if (rotate !== this.elem.getAttribute('transform')) {
                        this.elem.setAttribute('transform', rotate);
                    }
                }
            }
        });
    }

    /**
    * Reverses the stored change action.
    * @param {module:history.HistoryEventHandler} handler
    * @fires module:history~Command#event:history
    * @returns {void}
    */
    unapply(handler) {
        super.unapply(handler, () => {
            let bChangedTransform = false;
            Object.entries(this.oldValues).forEach(([attr, value]) => {
                if (attr === '#children') {
                    replaceChildren(this.elem, value);
                } else if (value) {
                    if (attr === '#text') {
                        this.elem.textContent = value.toString();
                    } else if (attr === '#href') {
                        setHref(this.elem, value.toString());
                    } else {
                        this.elem.setAttribute(attr, value.toString());
                    }
                } else if (attr === '#text') {
                    this.elem.textContent = '';
                } else {
                    this.elem.removeAttribute(attr);
                }
                if (attr === 'transform') { bChangedTransform = true; }
            });

            // relocate rotational transform, if necessary
            if (!bChangedTransform) {
                const angle = getRotationAngle(this.elem as SVGGraphicsElement);
                if (angle) {
                    const bbox = (this.elem as SVGGraphicsElement).getBBox();
                    const cx = bbox.x + bbox.width / 2,
                        cy = bbox.y + bbox.height / 2;
                    const rotate = ['rotate(', angle, ' ', cx, ',', cy, ')'].join('');
                    if (rotate !== this.elem.getAttribute('transform')) {
                        this.elem.setAttribute('transform', rotate);
                    }
                }
            }

            // Remove transformlist to prevent confusion that causes bugs like 575.
            removeElementFromListMap(this.elem);
        });
    }

    mergeHistory(other: Command, newText?: string): boolean {
        if (!(other instanceof ChangeElementCommand)) {
            return false;
        }

        if (other.elem !== this.elem) {
            return false;
        }

        if (Math.abs(other.timestamp.getTime() - this.timestamp.getTime()) > 1000) {
            return false;
        }

        for (const attr in other.oldValues) {
            this.newValues[attr] = other.newValues[attr];

            if (!(attr in this.oldValues)) {
                this.oldValues[attr] = other.oldValues[attr];
            }
        }

        if (newText) {
            this.text = newText;
        }

        this.timestamp = new Date();

        return true;
    }

    isNoOp() {
        if (this.oldValues.length != this.newValues.length) {
            return false;
        }

        for (const key in this.oldValues) {
            if (this.oldValues[key] !== this.newValues[key])
                return false;
        }

        return true;
    }
}
